import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import OrbitAgentBlankState from './OrbitAgentBlankState';
import { Breadcrumbs, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { paths } from 'src/common/config/TextMessage';
import { useNavigate } from 'react-router';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';

interface OrbitAgentSiteGuardProps {
  /** The content to render if a site is successfully added. */
  children: ReactNode;
  /** The title/name of the agent (e.g. "Keyword Assist") to be displayed in breadcrumbs and blank state. */
  title: string;
  /** Optional header element or actions bar to render in the top right of the breadcrumbs row. */
  headerComponent?: ReactNode;
  /** Optional parent breadcrumb label. Defaults to "Orbit AI". */
  parentTitle?: string;
  /** Optional parent breadcrumb navigation path. Defaults to paths.orbitAiAgent. */
  parentPath?: string;
}

/**
 * `OrbitAgentSiteGuard` is a guard component that checks if a Squarespace site
 * has been added to the user's account.
 *
 * **Behaviour:**
 * - If a site is added (`_id` is present in the Redux store), it renders its `children`.
 * - If no site is added, it renders a consistent page layout featuring breadcrumbs
 *   pointing to Orbit AI (or custom parent), the agent's dynamic title, an optional header actions component,
 *   and the `OrbitAgentBlankState` component explaining how to add a site.
 */
export default function OrbitAgentSiteGuard({
  children,
  title,
  headerComponent,
  parentTitle = 'Orbit AI',
  parentPath = paths.orbitAiAgent
}: OrbitAgentSiteGuardProps) {
  const { _id } = useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails) ?? {};
  const navigate = useNavigate();

  if (_id) {
    return <>{children}</>;
  }

  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              sx={keywordResearchDashBoardCss?.navigateNextIcon}
            />
          }
          aria-label="breadcrumb"
        >
          <Typography
            sx={{
              ...keywordResearchDashBoardCss?.breadCrumbLightText,
              cursor: 'pointer'
            }}
            onClick={() => navigate(parentPath)}
          >
            {parentTitle}
          </Typography>
          <Typography
            sx={keywordResearchDashBoardCss?.breadCrumbText}
          >
            {title}
          </Typography>
        </Breadcrumbs>
        {headerComponent}
      </div>
      <OrbitAgentBlankState title={title} />
    </div>
  );
}
